import { useState, ReactNode, useMemo, useEffect } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { BasePopup } from '../../dashboard/BasePopup';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { BarChart3, List, Activity, Clock, FileText } from 'lucide-react';
import { CompactControls } from './CompactControls';
import { ExportButton } from '../../../utils/export';
import { ExportFormat } from '../../../utils/export/types';
import { ControlOption } from './types';

type DrillDownView = 'chart' | 'table' | 'log';

interface DrillDownModalProps<T> {
  title: string;
  data: T[];
  isOpen: boolean;
  onClose: () => void;
  filters?: BaseAnalyticsFilter;
  children: (props: {
    data: T[];
    activeView: DrillDownView;
    showMetric: 'sessions' | 'minutes';
    onItemClick?: (item: T) => void;
  }) => ReactNode;
  onItemClick?: (item: T) => void;
  exportDataTransform?: (data: T[]) => any[];
  exportFileName?: string;
  icon?: ReactNode;
  className?: string;
  hideMetricControls?: boolean;
  defaultView?: DrillDownView;
  viewOptions?: Array<ControlOption<DrillDownView>>;
}

const BASE_VIEW_OPTIONS: Array<ControlOption<DrillDownView>> = [
  {
    value: 'chart',
    label: 'Chart',
    icon: <BarChart3 className="h-4 w-4" />,
  },
  {
    value: 'table',
    label: 'Table',
    icon: <List className="h-4 w-4" />,
  },
  {
    value: 'log',
    label: 'Log',
    icon: <FileText className="h-4 w-4" />,
  },
];

export function DrillDownModal<T>({
  title,
  data,
  isOpen,
  onClose,
  filters,
  children,
  onItemClick,
  exportDataTransform,
  exportFileName = 'drill-down-data',
  icon = <BarChart3 className="h-6 w-6" />,
  className = 'max-w-6xl',
  hideMetricControls = false,
  defaultView = 'chart',
  viewOptions,
}: DrillDownModalProps<T>) {
  const resolvedViewOptions = useMemo(() => {
    const options = viewOptions && viewOptions.length > 0 ? viewOptions : BASE_VIEW_OPTIONS;
    return options.filter(
      (option, index, self) => self.findIndex(item => item.value === option.value) === index,
    );
  }, [viewOptions]);

  const availableViews = useMemo(
    () => resolvedViewOptions.map(option => option.value),
    [resolvedViewOptions],
  );

  const resolvedDefaultView = useMemo<DrillDownView>(() => {
    if (availableViews.includes(defaultView)) {
      return defaultView;
    }

    return availableViews[0] ?? 'chart';
  }, [availableViews, defaultView]);

  const [activeView, setActiveView] = useState<DrillDownView>(resolvedDefaultView);
  const isMobile = useIsMobile();
  const [showMetric, setShowMetric] = useState<'sessions' | 'minutes'>('sessions');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setActiveView(resolvedDefaultView);
    setShowMetric('sessions');
  }, [isOpen, resolvedDefaultView]);

  const exportData = exportDataTransform ? exportDataTransform(data) : data;

  const getFilterSummary = () => {
    const parts = [];
    if (filters?.startDate) parts.push(`From: ${filters.startDate.toLocaleDateString()}`);
    if (filters?.endDate) parts.push(`To: ${filters.endDate.toLocaleDateString()}`);
    if (filters?.experienceTitle) parts.push(`Session: ${filters.experienceTitle}`);
    if (filters?.unitIds?.length) parts.push(`Units: ${filters.unitIds.length} selected`);
    return parts.length > 0 ? parts.join(' | ') : 'All data';
  };

  const metricOptions = useMemo(
    () => [
      {
        value: 'sessions' as const,
        label: 'Sessions',
        icon: <Activity className="h-4 w-4" />,
      },
      {
        value: 'minutes' as const,
        label: 'Minutes',
        icon: <Clock className="h-4 w-4" />,
      },
    ],
    [],
  );

  const shouldShowMetricControls = !hideMetricControls && activeView !== 'log';

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      icon={icon}
      className={className}
      variant="analytics"
      headerContent={
        <div className="flex items-center gap-2">
          {exportData.length > 0 && (
            <ExportButton
              data={exportData}
              config={{ fileName: exportFileName, date: true }}
              format={ExportFormat.CSV}
              className="text-[var(--anl-text)] transition-colors hover:text-[var(--anl-text-muted)]"
            />
          )}
        </div>
      }
    >
      <div className={`bg-[var(--anl-surface)] text-[var(--anl-text)] ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className={`flex gap-2 ${isMobile ? 'flex-col mb-3' : 'flex-row flex-wrap items-start justify-between mb-6'}`}>
          <div className="text-sm text-[var(--anl-text-muted)]">
            <span className="font-semibold text-[var(--anl-text)]">Filters:</span>{' '}
            {getFilterSummary()}
          </div>

          <CompactControls
            metricOptions={shouldShowMetricControls ? metricOptions : []}
            viewOptions={resolvedViewOptions}
            selectedMetric={showMetric}
            selectedView={activeView}
            onMetricChange={setShowMetric}
            onViewChange={setActiveView}
          />
        </div>

        <div>
          {children({
            data,
            activeView,
            showMetric,
            onItemClick,
          })}
        </div>
      </div>
    </BasePopup>
  );
}
